import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { ImagePlus, Loader2, X } from "lucide-react";
import { createAsset } from "../services/createAsset";
import type { AssetCategoryOption } from "./hooks/useAssetCategories";
import { useProjectZones } from "./hooks/useProjectZones";

interface AddAssetDialogProps {
  projectId: string;
  propertyId: string;
  categories: AssetCategoryOption[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a category (e.g. opened from a category section). */
  defaultCategoryId?: string;
  /** Extra hook for callers with their own caches (the QA review page). */
  onCreated?: () => void;
}

const NO_ZONE = "none";

function PhotoPicker({
  label,
  files,
  multiple,
  onAdd,
  onRemove,
  disabled,
}: {
  label: string;
  files: File[];
  multiple: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {files.map((file, idx) => (
          <div
            key={`${file.name}-${idx}`}
            className="group relative h-14 w-14 rounded-lg overflow-hidden border border-border/50"
          >
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="h-full w-full object-cover"
            />
            <button
              type="button"
              onClick={() => onRemove(idx)}
              disabled={disabled}
              className="absolute top-0 right-0 bg-background/80 rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition"
              aria-label={`Remove ${label} photo`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {(multiple || files.length === 0) && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-14 w-14 p-0"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            aria-label={`Add ${label} photo`}
          >
            <ImagePlus className="h-4 w-4" />
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          hidden
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []).filter((f) =>
              f.type.startsWith("image/")
            );
            if (picked.length > 0) onAdd(picked);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

export function AddAssetDialog({
  projectId,
  propertyId,
  categories,
  open,
  onOpenChange,
  defaultCategoryId,
  onCreated,
}: AddAssetDialogProps) {
  const queryClient = useQueryClient();
  const { data: zones = [] } = useProjectZones(open ? projectId : null);
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState(defaultCategoryId ?? "");
  const [zoneId, setZoneId] = useState(NO_ZONE);
  const [notes, setNotes] = useState("");
  const [nameplateFiles, setNameplateFiles] = useState<File[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);

  const submitting = progress !== null;
  const canSubmit = name.trim() !== "" && categoryId !== "" && !submitting;

  // Re-apply the caller's default category each time the dialog opens — the
  // component stays mounted across opens, so initial state alone isn't enough.
  useEffect(() => {
    if (open) setCategoryId(defaultCategoryId ?? "");
  }, [open, defaultCategoryId]);

  const reset = () => {
    setName("");
    setCategoryId(defaultCategoryId ?? "");
    setZoneId(NO_ZONE);
    setNotes("");
    setNameplateFiles([]);
    setPhotoFiles([]);
    setProgress(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setProgress("Starting…");
    try {
      await createAsset(
        {
          propertyId,
          categoryId,
          name: name.trim(),
          zoneId: zoneId === NO_ZONE ? null : zoneId,
          notes,
          nameplateFile: nameplateFiles[0] ?? null,
          photoFiles,
        },
        setProgress
      );
      toast.success("Asset created");
      queryClient.invalidateQueries({ queryKey: ["editable-assets", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["survey-assets", propertyId] });
      onCreated?.();
      reset();
      onOpenChange(false);
    } catch (err) {
      console.error("Asset creation failed:", err);
      toast.error(err instanceof Error ? err.message : "Failed to create asset");
      setProgress(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add asset</DialogTitle>
          <DialogDescription>
            Attaching a nameplate photo lets the AI pipeline fill in manufacturer,
            model and serial automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-asset-name" className="text-xs text-muted-foreground">
              Name *
            </Label>
            <Input
              id="new-asset-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Rooftop Unit 1"
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Category *</Label>
            <Select value={categoryId} onValueChange={setCategoryId} disabled={submitting}>
              <SelectTrigger aria-label="Asset category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Zone</Label>
            <Select
              value={zones.length === 0 ? "" : zoneId}
              onValueChange={setZoneId}
              disabled={submitting || zones.length === 0}
            >
              <SelectTrigger aria-label="Asset zone">
                <SelectValue
                  placeholder={zones.length === 0 ? "No zones defined for this project" : "No zone"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ZONE}>No zone</SelectItem>
                {zones.map((zone) => (
                  <SelectItem key={zone.id} value={zone.id}>
                    {zone.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <PhotoPicker
            label="Nameplate photo"
            files={nameplateFiles}
            multiple={false}
            onAdd={(files) => setNameplateFiles(files.slice(0, 1))}
            onRemove={() => setNameplateFiles([])}
            disabled={submitting}
          />

          <PhotoPicker
            label="Asset photos"
            files={photoFiles}
            multiple
            onAdd={(files) => setPhotoFiles((prev) => [...prev, ...files])}
            onRemove={(idx) => setPhotoFiles((prev) => prev.filter((_, i) => i !== idx))}
            disabled={submitting}
          />

          <div className="space-y-1.5">
            <Label htmlFor="new-asset-notes" className="text-xs text-muted-foreground">
              Notes
            </Label>
            <Textarea
              id="new-asset-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              disabled={submitting}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {progress}
              </>
            ) : (
              "Create asset"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
